import { computed, observable, reaction, runInAction } from "mobx"

import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"

import type { RuntimeConfig, UPreviewProcessesApiState } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IObservableValue } from "mobx"
import type { ChangeEvent, FocusEvent } from "react"

const log = getLogger("TaskPreviewPortModel")

export class TaskPreviewPortModel extends Model {
  readonly previewPort = observable.box(-1)
  readonly isSaving = observable.box(false)
  private debouncedSavePortTimeout: number | null = null

  readonly isLoading = computed(() => {
    return this.previewProcessesState.get() === null
  })

  readonly previewStatePort = computed(() => {
    const previewState = this.previewProcessesState.get()
    if (previewState?.className === "Running") {
      return previewState.previewPortStatus?.first ?? null
    }
    return null
  })

  readonly isWaitingForPreviewUpdate = computed(() => {
    const currentPort = this.previewPort.get()
    const previewPort = this.previewStatePort.get()
    return (this.isSaving.get() || previewPort !== currentPort) && currentPort > 0
  })

  constructor(
    private readonly queries: Queries,
    private readonly taskId: string,
    private readonly previewProcessesState: IObservableValue<UPreviewProcessesApiState | null>,
    private readonly serviceCommand: IObservableValue<RuntimeConfig.RunAppCommand | null>,
    private readonly runtimeConfigPort: IObservableValue<number>,
    readonly isPortBeingListened: IObservableValue<boolean>
  ) {
    super("TaskPreviewPortModel")

    runInAction(() => {
      this.previewPort.set(runtimeConfigPort.get())
    })

    // Sync with runtime config when it changes
    this.lifetime.add(reaction(
      () => runtimeConfigPort.get(),
      (port) => {
        runInAction(() => {
          this.previewPort.set(port)
        })
      }
    ), "TaskPreviewPortModel:runtimeConfigPort")
  }

  private async savePort(port: number): Promise<void> {
    console.warn(`Veda3, port ${port}`)

    if (port <= 0) return
    if (port === this.runtimeConfigPort.get()) return

    runInAction(() => {
      this.isSaving.set(true)
    })

    const serviceId = this.serviceCommand.get()?.id
    if (!serviceId) return

    try {
      console.warn(`Veda3, port ${port}`)
      await this.queries.apiClient.savePreviewPort(this.taskId, port, serviceId)
    } catch (error) {
      log.error("Failed to save preview port:", error)
    } finally {
      runInAction(() => {
        this.isSaving.set(false)
      })
    }
  }

  readonly handlePreviewPortChange = (event: ChangeEvent<HTMLInputElement>) => {
    const port = validatePortForChange(event.target.value)

    runInAction(() => {
      this.previewPort.set(port)
    })

    if (this.debouncedSavePortTimeout !== null) {
      clearTimeout(this.debouncedSavePortTimeout)
    }

    if (port > 0) {
      this.debouncedSavePortTimeout = window.setTimeout(() => {
        void this.savePort(port)
      }, 1000)
    }
  }

  readonly handlePreviewPortBlur = async (event: FocusEvent<HTMLInputElement>) => {
    if (this.debouncedSavePortTimeout !== null) {
      clearTimeout(this.debouncedSavePortTimeout)
      this.debouncedSavePortTimeout = null
    }

    const port = validatePortForChange(event.target.value)
    const validPort = port <= 0 ? this.runtimeConfigPort.get() : port

    runInAction(() => {
      this.previewPort.set(validPort)
    })

    await this.savePort(validPort)
  }
}

export function validatePortForChange(value: string): number {
  const newValue = parseInt(value, 10)
  let port = isNaN(newValue) ? -1 : newValue

  // Clamp to valid range: 1-65535
  if (port > 65535) {
    port = 65535
  } else if (port > 0 && port < 1) {
    port = 1
  }

  return port
}